import React, { useEffect, useRef, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import {
	FormControlLabel,
	Switch,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Checkbox,
	ListItemText,
} from "@material-ui/core";
import { Card, CardBody } from "../../../../_metronic/_partials/controls";
import {
	getEscapeRoomById,
	updateEscapeRoom,
	deleteEscapeRoom,
	publishEscapeRoomTemplate,
	setButtonCatalogSource,
} from "../../../../api/escape-room";
import { getTemplateCategories } from "../../../../api/template-category";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import EscapeRoomEditor from "./EscapeRoomEditor";

const STAFF_CREATOR_EMAILS = new Set(["admin@demo.com"]);
const STAFF_CREATOR_ROLES = new Set(["admin", "moderator"]);

function isStaffCreatedEscapeRoom(er) {
	const created = er?.createdBy;
	if (!created || typeof created !== "object") return false;
	const email = String(created.email || "").trim().toLowerCase();
	const role = String(created.role || "").trim().toLowerCase();
	return STAFF_CREATOR_EMAILS.has(email) || STAFF_CREATOR_ROLES.has(role);
}

function isTemplateAdvanced(er) {
	if (!er?.templatePublished) return false;
	if (er.templateBasic === true) return false;
	if (er.templatePremium === true) return true;
	return isStaffCreatedEscapeRoom(er);
}

function categoryIdsOf(er) {
	const out = [];
	const seen = new Set();
	const push = (ref) => {
		if (ref == null || ref === "") return;
		const id = typeof ref === "object" && ref._id != null ? String(ref._id) : String(ref);
		if (!id || seen.has(id)) return;
		seen.add(id);
		out.push(id);
	};
	if (Array.isArray(er?.templateCategories)) er.templateCategories.forEach(push);
	push(er?.templateCategory);
	return out;
}

export default function EscapeRoomEditPage() {
	const { id } = useParams();
	const history = useHistory();
	const location = useLocation();
	const fromTemplatePicker = Boolean(location.state?.fromTemplatePicker);
	const [escapeRoom, setEscapeRoom] = useState(null);
	const [categories, setCategories] = useState([]);
	const [loading, setLoading] = useState(false);
	const [deleteConfirm, setDeleteConfirm] = useState(false);
	const [loadError, setLoadError] = useState(false);
	const pendingSaveRef = useRef(null);
	const mountedRef = useRef(true);
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		getTemplateCategories({ all: true })
			.then((res) => setCategories(Array.isArray(res.data) ? res.data : []))
			.catch(() => setCategories([]));
	}, []);

	useEffect(() => {
		if (!id) return;
		let cancelled = false;
		setLoadError(false);
		getEscapeRoomById(id)
			.then((res) => {
				if (cancelled) return;
				if (res.status === 200 && res.data) {
					setEscapeRoom(res.data);
				} else {
					setLoadError(true);
				}
			})
			.catch(() => {
				if (cancelled) return;
				alertError({ customMessage: "No se encontró el escape room." });
				setLoadError(true);
			});
		return () => {
			cancelled = true;
		};
	}, [id]);

	const runSave = (data, opts) => {
		if (!id) return;
		setLoading(true);
		updateEscapeRoom(id, data)
			.then((res) => {
				if (!mountedRef.current) return;
				if (res.status === 200) {
					if (!opts.silent) alertSuccess({ customMessage: "Guardado." });
					setEscapeRoom(res.data);
				}
			})
			.catch((err) => {
				if (mountedRef.current) {
					alertError({ error: err, customMessage: "No se pudo guardar." });
				}
			})
			.finally(() => {
				if (!mountedRef.current) return;
				setLoading(false);
				const pending = pendingSaveRef.current;
				pendingSaveRef.current = null;
				if (pending) {
					runSave(pending.data, pending.opts);
				}
			});
	};

	const handleSave = (data, opts = {}) => {
		if (!id) return;
		if (loading) {
			pendingSaveRef.current = { data, opts };
			return;
		}
		runSave(data, opts);
	};

	const handleDelete = () => {
		if (!id) return;
		if (escapeRoom?.buttonCatalogSource) {
			alertError({
				customMessage:
					"Este escape room es la fuente del catálogo de botones y no se puede eliminar. Elige otro ER como fuente antes de borrarlo.",
			});
			setDeleteConfirm(false);
			return;
		}
		deleteEscapeRoom(id)
			.then((res) => {
				if (res.status === 204) {
					alertSuccess({ customMessage: "Escape room eliminado." });
					history.push("/dashboard");
				}
			})
			.catch((err) => {
				const code = err?.response?.data?.code;
				alertError({
					error: err,
					customMessage:
						code === "ESCAPE_ROOM_PROTECTED"
							? "Este escape room es la fuente del catálogo de botones y no se puede eliminar."
							: "No se pudo eliminar.",
				});
			});
	};

	const handleToggleTemplatePublished = () => {
		if (!id || !escapeRoom) return;
		const next = !Boolean(escapeRoom.templatePublished);
		publishEscapeRoomTemplate(id, {
			templatePublished: next,
			templatePremium: next ? true : false,
			templateBasic: false,
		})
			.then((res) => {
				if (res.status === 200 && res.data) {
					setEscapeRoom(res.data);
					alertSuccess({ customMessage: next ? "Publicado como plantilla." : "Plantilla despublicada." });
				}
			})
			.catch((err) => {
				alertError({ error: err, customMessage: "No se pudo actualizar la publicación de plantilla." });
			});
	};

	const handleToggleTemplatePremium = () => {
		if (!id || !escapeRoom || !escapeRoom.templatePublished) return;
		const nextAdvanced = !isTemplateAdvanced(escapeRoom);
		publishEscapeRoomTemplate(id, {
			templatePublished: true,
			templatePremium: nextAdvanced,
			templateBasic: !nextAdvanced,
		})
			.then((res) => {
				if (res.status === 200 && res.data) {
					setEscapeRoom(res.data);
					alertSuccess({
						customMessage: nextAdvanced
							? "Marcada como plantilla avanzada."
							: "Plantilla disponible para todos los planes (Free).",
					});
				}
			})
			.catch((err) => {
				alertError({ error: err, customMessage: "No se pudo actualizar el tipo de plantilla." });
			});
	};

	const handleChangeTemplateCategories = (event) => {
		if (!id || !escapeRoom) return;
		const raw = event.target.value;
		const next = Array.isArray(raw) ? raw.map(String) : [];
		setEscapeRoom((prev) =>
			prev
				? {
						...prev,
						templateCategories: next.map((cid) => {
							const cat = categories.find((c) => String(c._id) === String(cid));
							return cat || cid;
						}),
						templateCategory: null,
				  }
				: prev
		);
		publishEscapeRoomTemplate(id, {
			templateCategories: next,
		})
			.then((res) => {
				if (res.status === 200 && res.data) {
					setEscapeRoom(res.data);
					alertSuccess({ customMessage: "Tipos de plantilla actualizados." });
				}
			})
			.catch((err) => {
				alertError({ error: err, customMessage: "No se pudieron actualizar los tipos." });
				getEscapeRoomById(id).then((r) => {
					if (r.status === 200 && r.data) setEscapeRoom(r.data);
				});
			});
	};

	const handleSetButtonCatalogSource = () => {
		if (!id || !escapeRoom || escapeRoom.buttonCatalogSource) return;
		setButtonCatalogSource(id)
			.then((res) => {
				if (res.status === 200 && res.data) {
					setEscapeRoom(res.data);
					alertSuccess({
						customMessage:
							"Este escape room es ahora la fuente del catálogo de botones del editor. No se puede eliminar mientras esté marcado.",
					});
				}
			})
			.catch((err) => {
				alertError({ error: err, customMessage: "No se pudo marcar como catálogo de botones." });
			});
	};

	if (!escapeRoom) {
		return (
			<Card>
				<CardBody>{loadError ? "No se encontró el escape room." : "Cargando…"}</CardBody>
			</Card>
		);
	}

	return (
		<>
			{fromTemplatePicker && !escapeRoom.templatePublished ? (
				<Card className="mb-3">
					<CardBody style={{ background: "#faf5ff", borderLeft: "4px solid #7c3aed" }}>
						<div style={{ fontWeight: 800, color: "#5b21b6", marginBottom: 4 }}>
							Publicar escape room de usuario
						</div>
						<div style={{ color: "#64748b", fontSize: 14, lineHeight: 1.5 }}>
							Revisa el contenido en el editor. Cuando esté listo, pulsa{" "}
							<strong>Publicar como plantilla</strong> para añadirlo al catálogo global.
						</div>
					</CardBody>
				</Card>
			) : null}
			<Card>
				<CardBody>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
						<div style={{ fontWeight: 700 }}>
							Plantilla global:{" "}
							<span style={{ color: escapeRoom.templatePublished ? "#16a34a" : "#64748b" }}>
								{escapeRoom.templatePublished ? "Publicada" : "No publicada"}
							</span>
							{escapeRoom.templatePublished && isTemplateAdvanced(escapeRoom) ? (
								<span
									style={{
										marginLeft: 10,
										fontSize: 11,
										fontWeight: 900,
										color: "#fff",
										background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
										padding: "3px 10px",
										borderRadius: 999,
										verticalAlign: "middle",
									}}
								>
									Premium
								</span>
							) : escapeRoom.templatePublished ? (
								<span
									style={{
										marginLeft: 10,
										fontSize: 11,
										fontWeight: 800,
										color: "#64748b",
										background: "#f1f5f9",
										padding: "3px 10px",
										borderRadius: 999,
										verticalAlign: "middle",
									}}
								>
									Básica
								</span>
							) : null}
						</div>
						<button
							type="button"
							className="btn btn-sm btn-primary"
							onClick={handleToggleTemplatePublished}
							disabled={loading}
						>
							{escapeRoom.templatePublished ? "Despublicar" : "Publicar"}
						</button>
					</div>
					{escapeRoom.templatePublished ? (
						<div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #e5e7eb" }}>
							<FormControlLabel
								control={
									<Switch
										color="primary"
										checked={isTemplateAdvanced(escapeRoom)}
										onChange={handleToggleTemplatePremium}
										disabled={loading}
									/>
								}
								label={
									<span style={{ fontWeight: 600 }}>
										Plantilla avanzada (requiere la feature Plantillas en el plan del usuario; todos pueden previsualizarla)
									</span>
								}
							/>
							<div style={{ marginTop: 16 }}>
								<FormControl variant="outlined" size="small" style={{ minWidth: 280 }} disabled={loading}>
									<InputLabel id="tpl-categories-label">Tipos de plantilla</InputLabel>
									<Select
										labelId="tpl-categories-label"
										label="Tipos de plantilla"
										multiple
										value={categoryIdsOf(escapeRoom)}
										onChange={handleChangeTemplateCategories}
										renderValue={(selected) => {
											const ids = Array.isArray(selected) ? selected : [];
											if (!ids.length) return "Sin tipo";
											return ids
												.map((cid) => categories.find((c) => String(c._id) === String(cid))?.name || cid)
												.join(", ");
										}}
									>
										{categories.map((cat) => {
											const cid = String(cat._id);
											const checked = categoryIdsOf(escapeRoom).includes(cid);
											return (
												<MenuItem key={cid} value={cid}>
													<Checkbox color="primary" checked={checked} />
													<ListItemText
														primary={`${cat.name}${cat.active === false ? " (inactivo)" : ""}`}
													/>
												</MenuItem>
											);
										})}
									</Select>
								</FormControl>
								<div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
									Puedes asignar varios tipos. Gestiona el catálogo en{" "}
									<strong>Plantillas → Tipos de plantilla</strong>.
								</div>
							</div>
						</div>
					) : null}
				</CardBody>
			</Card>

			<Card className="mt-3">
				<CardBody>
					<div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
						<div style={{ flex: "1 1 280px" }}>
							<div style={{ fontWeight: 700, marginBottom: 4 }}>
								Catálogo de botones del editor:{" "}
								<span style={{ color: escapeRoom.buttonCatalogSource ? "#16a34a" : "#64748b" }}>
									{escapeRoom.buttonCatalogSource ? "Este escape room" : "Otro / no asignado"}
								</span>
							</div>
							<div style={{ color: "#64748b", fontSize: 14, lineHeight: 1.5 }}>
								Los botones de este ER aparecen como plantillas en el panel <strong>Elementos → Botón</strong> del
								portal. Solo puede haber uno y <strong>no se puede eliminar</strong> mientras esté marcado.
							</div>
						</div>
						{escapeRoom.buttonCatalogSource ? (
							<span
								style={{
									fontSize: 12,
									fontWeight: 800,
									color: "#5b21b6",
									background: "#f5f3ff",
									border: "1px solid #ddd6fe",
									padding: "8px 12px",
									borderRadius: 8,
									whiteSpace: "nowrap",
								}}
							>
								Fuente activa · protegido
							</span>
						) : (
							<button
								type="button"
								className="btn btn-sm btn-primary"
								onClick={handleSetButtonCatalogSource}
								disabled={loading}
							>
								Usar como catálogo de botones
							</button>
						)}
					</div>
				</CardBody>
			</Card>

			<EscapeRoomEditor
				key={escapeRoom._id}
				escapeRoom={escapeRoom}
				onSave={handleSave}
				onDelete={escapeRoom.buttonCatalogSource ? undefined : () => setDeleteConfirm(true)}
				saving={loading}
			/>

			<ConfirmDialog
				title="¿Eliminar este escape room?"
				open={deleteConfirm}
				setOpen={setDeleteConfirm}
				onConfirm={handleDelete}
			/>
		</>
	);
}
